import classNames from "classnames";
import { useCombobox } from "downshift";
import { Ref, useEffect, useRef, useState } from "react";
import { useCompany } from "../context/companyContext";
import useOnClickOutside from "../hooks/useOnClickOutside";
import useToggle from "../hooks/useToggle";
import { MD, useCompanies } from "../http/monatsdatenApi";
import SVGClose from "../icons/Close.svg?react";
import SVGExpandLess from "../icons/ExpandLess.svg?react";
import SVGExpandMore from "../icons/ExpandMore.svg?react";
import SVGSearch from "../icons/Search.svg?react";
import { getRemoteDataStatus } from "../utils";
import styles from "./CompanySelect.module.css";
import Badge from "./ui/Badge";
import ErrorText from "./ui/ErrorText";
import HighlightText from "./ui/HighlightText";
import Icon from "./ui/Icon";
import Input from "./ui/Input";
import LoadingSpinner from "./ui/LoadingSpinner";

interface Props {
  className?: string;
}

const CompanySelect = ({ className }: Props) => {
  const { company, hasOnlyOneCompany, status, setCompanyId } = useCompany();
  const menu = useToggle();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useOnClickOutside(searchRef, menu.close);

  useEffect(() => {
    if (!menu.isOpen) return;

    inputRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Tab") menu.close();
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menu.isOpen]);

  return (
    <div
      className={classNames(styles.companySelect, className, {
        [styles.companySelectOpen]: menu.isOpen,
      })}
      ref={searchRef}
    >
      {status === "validating" ? (
        <LoadingSpinner
          className={styles.loadingSpinner}
          delayed={175}
          size="small"
        />
      ) : hasOnlyOneCompany && company ? (
        <CompanyInfo company={company} />
      ) : (
        <button
          type="button"
          className={classNames(styles.button, {
            [styles.buttonOpen]: menu.isOpen,
          })}
          onClick={menu.toggle}
        >
          <CompanyInfo company={company} />
          <Icon
            glyph={menu.isOpen ? SVGExpandLess : SVGExpandMore}
            className={styles.expandIcon}
          />
        </button>
      )}

      {menu.isOpen && (
        <CompanySelectMenu
          ref={inputRef}
          onSelectedItemChange={(c) => {
            menu.close();
            setCompanyId(c.id);
          }}
        />
      )}
    </div>
  );
};

interface CompanyInfoProps {
  company: MD.Company | undefined;
}

const CompanyInfo = ({ company }: CompanyInfoProps) => (
  <div className={styles.companyInfo}>
    {company ? (
      <>
        <span className={styles.companyName}>{company.name}</span>
        <span className={styles.memberName}>{company.memberName}</span>
      </>
    ) : (
      "Wählen Sie eine Betriebsstätte aus …"
    )}
  </div>
);

interface CompanySelectMenuProps {
  ref: Ref<HTMLInputElement>;
  onSelectedItemChange: (item: MD.Company) => void;
}

const CompanySelectMenu = ({
  ref,
  onSelectedItemChange,
}: CompanySelectMenuProps) => {
  const [inputValue, setInputValue] = useState("");

  const aborter = useRef(new AbortController());
  const { data, isValidating, error } = useCompanies(
    { q: inputValue, page: 1, limit: 10 },
    aborter.current.signal,
  );
  const items = data?.items ?? [];
  const status = getRemoteDataStatus({ isValidating, error });

  const { getMenuProps, getInputProps, highlightedIndex, getItemProps } =
    useCombobox({
      defaultIsOpen: true,
      items,
      itemToString: (item) => (item ? item.name : ""),
      inputValue,
      onStateChange: ({ inputValue, type, selectedItem }) => {
        switch (type) {
          case useCombobox.stateChangeTypes.InputChange:
            aborter.current.abort();
            aborter.current = new AbortController();
            setInputValue(inputValue ?? "");
            break;
          case useCombobox.stateChangeTypes.InputKeyDownEnter:
          case useCombobox.stateChangeTypes.ItemClick:
          case useCombobox.stateChangeTypes.InputBlur:
            if (!selectedItem) return;
            setInputValue(selectedItem.name);
            onSelectedItemChange(selectedItem);
            break;
          default:
            break;
        }
      },
    });

  return (
    <div className={styles.menu}>
      <hr className={styles.separator} />
      <div className={styles.search}>
        <div className={styles.combobox}>
          <Input
            {...getInputProps({ ref })}
            type="search"
            placeholder="Suche Betriebsstätten …"
          />
        </div>
        {inputValue === "" && (
          <Icon glyph={SVGSearch} className={styles.searchIcon} />
        )}
        {inputValue !== "" && (
          <Icon
            glyph={SVGClose}
            className={styles.clearIcon}
            onClick={() => setInputValue("")}
          />
        )}
      </div>
      <span className={styles.title}>Betriebsstätten</span>
      <dl {...getMenuProps()} className={styles.suggestions}>
        {status === "success" && items.length === 0 && (
          <p className={styles.emptyText}>
            {inputValue !== "" ? (
              <span>
                Es wurden keine mit Ihrer Suchanfrage –{" "}
                <strong>{inputValue}</strong> – übereinstimmenden
                Betriebsstätten gefunden.
              </span>
            ) : (
              <span>Keine Betriebsstätten gefunden.</span>
            )}
          </p>
        )}

        {status === "validating" && (
          <div className={styles.status}>
            <LoadingSpinner delayed={175} />
          </div>
        )}

        {status === "failure" && (
          <div className={styles.status}>
            <ErrorText text="Fehler beim Laden der Betriebsstätten." />
          </div>
        )}

        {data?.items.map((item, index) => (
          <dd
            className={classNames(styles.suggestion, {
              [styles.highlightedSuggestion]: index === highlightedIndex,
            })}
            key={index}
            {...getItemProps({
              item,
              index,
            })}
          >
            <div className={styles.companyInfo}>
              <HighlightText
                className={styles.companyName}
                text={item.name}
                highlight={inputValue}
              />
              <HighlightText
                className={styles.memberName}
                text={item.memberName}
                highlight={inputValue}
              />
            </div>
            {inputValue === String(item.id) && (
              <Badge>
                <span className={styles.matching}>Übereinstimmende </span>
                Betriebsstättennummer
              </Badge>
            )}
            {inputValue === String(item.memberId) && (
              <Badge>
                <span className={styles.matching}>Übereinstimmende </span>
                Mitgliedsnummer
              </Badge>
            )}
          </dd>
        ))}
      </dl>
    </div>
  );
};

export default CompanySelect;
